import { useEffect, useMemo } from "react";
import { NetworkStatus } from "@apollo/client";
import { Flex } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { type MinimalQueryResult } from "@jsq/ds/types";
import { useCountryOptions } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/AddressFields/CountryFormField/useCountryOptions";
import { ProfileToInvestorView } from "~/pages/Compliance/InvestorCaseNavigation/InvestorCaseWizard/ProfileStep/ProfileToInvestorView";
import { SummaryCard } from "~/pages/Compliance/InvestorCaseNavigation/InvestorCaseWizard/ReviewStep/SummaryCard";
import { useEntityClientWizard } from "../context";
import * as styles from "../styles_vanilla.css";
import { CaseInfoSummaryCard } from "./CaseInfoSummaryCard";
import { ReviewFooter } from "./ReviewFooter";
import { buildOnboardingProfileMatchData } from "./utils";

const SETTLED_REVIEW_QUERY = {
  loading: false,
  data: {},
  networkStatus: NetworkStatus.ready,
} as MinimalQueryResult;

export const ReviewStep = () => {
  const {
    caseInfo,
    caseType,
    newProfileData,
    productLine,
    goBack,
    goToStep,
    setFooterContent,
  } = useEntityClientWizard();
  const { countryOptions } = useCountryOptions();

  const hasData = Boolean(caseInfo && newProfileData);

  useEffect(() => {
    if (!hasData) {
      setFooterContent(null);
      return undefined;
    }
    setFooterContent(<ReviewFooter />);
    return () => setFooterContent(null);
  }, [hasData, setFooterContent]);

  const matchData = useMemo(() => {
    if (!caseInfo || !newProfileData) return null;
    return buildOnboardingProfileMatchData({
      clientName: caseInfo.clientName,
      profile: newProfileData,
      productLineFromMain: productLine,
      resolveCountryName: (code) =>
        countryOptions.find((option) => option.code === code)?.name ?? code ?? null,
      countryNameById: Object.fromEntries(
        countryOptions.map((option) => [String(option.id), option.name]),
      ),
    });
  }, [caseInfo, newProfileData, productLine, countryOptions]);

  if (!caseInfo || !newProfileData || !matchData) return null;

  return (
    <Flex flexDirection="column" gap={Sizes.LG} className={styles.stepWrapper}>
      <CaseInfoSummaryCard
        caseInfo={caseInfo}
        caseType={caseType}
        onBack={goBack}
        onEdit={() => goToStep("case-information")}
      />
      <SummaryCard
        title="Compliance profile"
        subtitle="Review the profile to assign to this onboarding case."
        onEdit={() => goToStep("profile")}
      >
        <ProfileToInvestorView
          matchData={matchData}
          queryResult={SETTLED_REVIEW_QUERY}
          highlightProfileColumn={false}
        />
      </SummaryCard>
    </Flex>
  );
};
